import { parseISO, startOfWeek, format } from "date-fns";

export const fmtAUD = (n) => {
  const v = Number(n || 0);
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);
};

export const fmtSigned = (n) => {
  const v = Number(n || 0);
  const s = fmtAUD(Math.abs(v));
  if (v > 0) return `+${s}`;
  if (v < 0) return `-${s}`;
  return s;
};

export const pct = (part, whole) => {
  if (!whole) return 0;
  return (part / whole) * 100;
};

// period key + label for a transaction date
export function periodInfo(dateStr, mode) {
  const d = parseISO(dateStr);
  if (mode === "weekly") {
    const ws = startOfWeek(d, { weekStartsOn: 1 }); // Monday
    return { key: format(ws, "yyyy-MM-dd"), label: format(ws, "dd MMM") };
  }
  return { key: format(d, "yyyy-MM"), label: format(d, "MMM yyyy") };
}

export function filterBySource(txns, source) {
  if (source === "all") return txns;
  return txns.filter((t) => t.source === source);
}

export function totals(txns) {
  let income = 0;
  let expense = 0;
  for (const t of txns) {
    if (t.amount >= 0) income += t.amount;
    else expense += Math.abs(t.amount);
  }
  const net = income - expense;
  return { income, expense, net, margin: income ? (net / income) * 100 : 0 };
}

export function periodSeries(txns, mode) {
  const map = new Map();
  for (const t of txns) {
    const { key, label } = periodInfo(t.date, mode);
    if (!map.has(key)) map.set(key, { key, label, income: 0, expense: 0 });
    const row = map.get(key);
    if (t.amount >= 0) row.income += t.amount;
    else row.expense += Math.abs(t.amount);
  }
  const rows = Array.from(map.values()).sort((a, b) => (a.key < b.key ? -1 : 1));
  return rows.map((r) => ({
    ...r,
    income: Math.round(r.income * 100) / 100,
    expense: Math.round(r.expense * 100) / 100,
    net: Math.round((r.income - r.expense) * 100) / 100,
  }));
}

export function categoryBreakdown(txns, type) {
  const isIncome = type === "income";
  const filtered = txns.filter((t) =>
    isIncome ? t.amount >= 0 : t.amount < 0
  );
  const total = filtered.reduce((s, t) => s + Math.abs(t.amount), 0);
  const map = new Map();
  for (const t of filtered) {
    if (!map.has(t.category)) map.set(t.category, { category: t.category, amount: 0, count: 0 });
    const row = map.get(t.category);
    row.amount += Math.abs(t.amount);
    row.count += 1;
  }
  return Array.from(map.values())
    .map((r) => ({ ...r, amount: Math.round(r.amount * 100) / 100, share: total ? (r.amount / total) * 100 : 0 }))
    .sort((a, b) => b.amount - a.amount);
}

export function sourceSummary(txns) {
  const map = new Map();
  for (const t of txns) {
    if (!map.has(t.source)) map.set(t.source, { source: t.source, income: 0, expense: 0 });
    const row = map.get(t.source);
    if (t.amount >= 0) row.income += t.amount;
    else row.expense += Math.abs(t.amount);
  }
  return Array.from(map.values())
    .map((r) => ({ ...r, net: r.income - r.expense }))
    .sort((a, b) => {
      if (a.source === "Account") return -1;
      if (b.source === "Account") return 1;
      return a.source.localeCompare(b.source);
    });
}

export function detectSources(txns) {
  const set = new Set(txns.map((t) => t.source));
  const arr = Array.from(set);
  arr.sort((a, b) => {
    if (a === "Account") return -1;
    if (b === "Account") return 1;
    return a.localeCompare(b);
  });
  return arr;
}
